import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Lottie from 'lottie-light-react';
import { DateTime } from 'luxon';
import React, { useMemo, useRef } from 'react';
import { RxCross2 } from 'react-icons/rx';
import animationData from '../assets/animation_streak.json';
import LootBox from '../Components/LootBox';
import { Streak } from '../Types/Streak';
import { UserLootBox } from '../Types/UserLootBox';

type StreakModalProps = {
  onClose: () => void;
  streaks: Streak[];
  title: string;
  description: string;
  lootBox?: UserLootBox;
};

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const isStreakActiveOnDay = (streak: Streak, day: DateTime): boolean => {
  const streakStartDate = DateTime.fromJSDate(streak.startAt)
    .setZone(window.env.REACT_APP_DEFAULT_TIMEZONE!)
    .startOf('day');

  const streakLastExtendedDate = DateTime.fromJSDate(streak.lastExtendedAt)
    .setZone(window.env.REACT_APP_DEFAULT_TIMEZONE!)
    .startOf('day');

  return day >= streakStartDate && day <= streakLastExtendedDate;
};

const StreakModal: React.FC<StreakModalProps> = ({
  streaks,
  title,
  description,
  onClose,
  lootBox,
}) => {
  const lottieRef = useRef(null);

  const today = useMemo(
    () =>
      DateTime.now()
        .setZone(window.env.REACT_APP_DEFAULT_TIMEZONE!)
        .startOf('day'),
    [],
  );

  /**
   * Checks if a streak is currently active.
   * A streak is considered active if it was extended either today or yesterday.
   * This gives users a full day to extend their streak - if they read on Monday,
   * they have until the end of Tuesday to read again before the streak breaks.
   */
  const isStreakActive = (streak: Streak): boolean => {
    return (
      isStreakActiveOnDay(streak, today) ||
      isStreakActiveOnDay(streak, today.minus({ days: 1 }))
    );
  };

  const currentStreak =
    streaks?.find((streak) => isStreakActive(streak)) ?? null;

  const handleComplete = () => {
    if (lottieRef.current) {
      lottieRef.current.goToAndPlay(0, true);
    }
  };

  const todayIndex = today.weekday - 1; // 0 for Monday, 6 for Sunday
  const startOfWeek = today.startOf('week');

  const isDayChecked = (dayIndex: number): boolean => {
    if (!streaks?.length) {
      return false;
    }

    const day = startOfWeek.plus({ days: dayIndex });
    return streaks.some((streak) => isStreakActiveOnDay(streak, day));
  };

  const getNextRewardMessage = (streak: Streak): string => {
    const currentStreakLength = streak.currentStreakLength;
    const isExtendedToday = isStreakActiveOnDay(streak, today);

    if (currentStreakLength < 3) {
      const daysLeft = 3 - currentStreakLength;
      if (daysLeft === 1 && !isExtendedToday) {
        return 'Extend your streak today to earn a Bronze loot box!';
      }
      return `Keep going! You'll earn a Bronze loot box in ${daysLeft} day${daysLeft > 1 ? 's' : ''}!`;
    }

    if (currentStreakLength < 7) {
      const daysLeft = 7 - currentStreakLength;
      if (daysLeft === 1 && !isExtendedToday) {
        return 'Extend your streak today to earn a Silver loot box!';
      }
      return `Keep going! You'll earn a Silver loot box in ${daysLeft} day${daysLeft > 1 ? 's' : ''}!`;
    }

    const daysUntilNextSilver = 7 - (currentStreakLength % 7);
    if (daysUntilNextSilver === 1 && !isExtendedToday) {
      return 'Extend your streak today to earn another Silver loot box!';
    }
    return `Keep going! You'll earn another Silver loot box in ${daysUntilNextSilver} day${daysUntilNextSilver > 1 ? 's' : ''}!`;
  };

  return (
    <div className="flex flex-col items-center justify-center w-full p-4">
      <button
        data-testid="close-streak-modal"
        onClick={onClose}
        className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
      >
        <RxCross2 className="w-6 h-6" />
      </button>
      <div className="flex flex-col gap-3 p-4 max-sm:p-2 max-sm:gap-1 bg-white rounded-2xl border-solid border-gray-200 justify-between items-center">
        <Lottie
          lottieRef={lottieRef}
          loop={false}
          autoplay={true}
          animationData={animationData}
          rendererSettings={{ preserveAspectRatio: 'xMidYMid slice' }}
          onComplete={handleComplete}
          style={{ width: '15%', height: '15%' }}
        />
        <div
          className="text-center text-[#FF6723] text-2xl font-['Nunito Sans']"
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <div className="flex justify-center gap-2">
          {daysOfWeek.map((day, index) => (
            <div
              key={day}
              className={`flex flex-col items-center p-2 rounded ${
                index === todayIndex ? 'bg-[#FFE5B9]' : ''
              }`}
            >
              <span
                className={`text-sm mb-1 font-bold ${
                  index === todayIndex ? 'text-black' : 'text-gray-500'
                }`}
              >
                {day}
              </span>
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  isDayChecked(index)
                    ? 'bg-white text-[#FF6723]'
                    : index === todayIndex
                      ? 'border-gray-300 bg-[#FFB02E]'
                      : 'border-gray-300 border-2'
                }`}
              >
                {isDayChecked(index) && (
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="text-[#FF6723] text-2xl"
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="text-center text-gray-500 font-['Nunito Sans']">
          {description}
        </div>
        {lootBox && <LootBox lootBox={lootBox} />}
        {currentStreak && (
          <div className="mt-2 text-center text-[#FF6723] font-semibold">
            {getNextRewardMessage(currentStreak)}
          </div>
        )}
      </div>
    </div>
  );
};

export default StreakModal;
